import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Modal, ModalHeader, ModalBody,
  ModalFooter, Spinner, Input, Label, FormGroup, Alert
} from "reactstrap";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { API_BASE, getAuthHeaders } from "../../../helpers/apiConfig";
// OData naive-UTC datetime'ları +03:00 etiketiyle döndürüyor; ham new Date()
// kullanmak saatleri 3 saat geriden gösterir (bkz. helpers/apiDate.js).
import { formatApiDateTime } from "../../../helpers/apiDate";

/**
 * Haber Kaynakları — "Bilgi'ye Gel" akışını besleyen RSS tanımları.
 *
 * Kaynak pasife alınınca İKİ şey olur:
 *   1) Hangfire job (HaberJobs) o beslemeyi artık çekmez,
 *   2) o kaynaktan DAHA ÖNCE çekilmiş haberler de uygulamada görünmez
 *      (HaberController listeyi kaynak aktifliğine göre süzer).
 * Yani telif şikâyeti / kalitesiz besleme durumunda kaynağı pasife almak
 * anında ve tam bir müdahaledir; silmeye gerek yoktur.
 *
 * SON_CEKIM / SON_HATA job'ın yazdığı teşhis alanlarıdır — düzenlenemez.
 * "Ölü kaynak" (RSS adresi değişmiş, site kapanmış) buradan görülür.
 */

const BASE_URL = API_BASE;

const KATEGORI_SECENEKLERI = ["Tarım", "Hayvancılık", "Piyasa", "Destek"];

const bosForm = {
  ID: null, KOD: "", AD: "", RSS_URL: "", SITE_URL: "",
  KATEGORI: "", LOGO_URL: "", SIRA: 0, AKTIF: true,
};

const HaberKaynaklari = () => {
  document.title = "Haber Kaynakları | HarmanaGEL";

  const [kaynaklar, setKaynaklar] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const [modal, setModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [formData, setFormData] = useState(bosForm);

  const bildir = (mesaj) => {
    setSuccessMsg(mesaj);
    setTimeout(() => setSuccessMsg(""), 3000);
  };

  /** OData hata gövdesinden okunabilir mesaj çıkarır (BadRequest metinleri burada). */
  const hataMesaji = async (response, varsayilan) => {
    try {
      const metin = await response.text();
      if (!metin) return varsayilan;
      try {
        const json = JSON.parse(metin);
        return json?.error?.message || json?.Message || json?.message || varsayilan;
      } catch {
        return metin;
      }
    } catch {
      return varsayilan;
    }
  };

  const fetchKaynaklar = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${BASE_URL}/HABER_KAYNAKLARI?$orderby=SIRA asc,AD asc`,
        { headers: getAuthHeaders() }
      );
      if (!response.ok) throw new Error(await hataMesaji(response, `HTTP ${response.status}`));
      const result = await response.json();
      setKaynaklar(result?.value || []);
    } catch (err) {
      setError(err.message || "Haber kaynakları yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchKaynaklar(); }, [fetchKaynaklar]);

  const toggleModal = () => {
    setModal((acik) => {
      if (acik) {
        setFormData(bosForm);
        setIsEditing(false);
      }
      return !acik;
    });
  };

  const handleEdit = (kaynak) => {
    setFormData({
      ID: kaynak.ID,
      KOD: kaynak.KOD || "",
      AD: kaynak.AD || "",
      RSS_URL: kaynak.RSS_URL || "",
      SITE_URL: kaynak.SITE_URL || "",
      KATEGORI: kaynak.KATEGORI || "",
      LOGO_URL: kaynak.LOGO_URL || "",
      SIRA: kaynak.SIRA ?? 0,
      AKTIF: kaynak.AKTIF !== false,
    });
    setIsEditing(true);
    setModal(true);
  };

  const handleSave = async () => {
    if (!formData.KOD.trim()) return setError("KOD zorunludur.");
    if (!formData.AD.trim()) return setError("Kaynak adı zorunludur.");
    if (!formData.RSS_URL.trim()) return setError("RSS adresi zorunludur.");

    setError(null);
    setKaydediliyor(true);
    try {
      const payload = {
        KOD: formData.KOD.trim().toUpperCase(),
        AD: formData.AD.trim(),
        RSS_URL: formData.RSS_URL.trim(),
        SITE_URL: formData.SITE_URL.trim() || null,
        KATEGORI: formData.KATEGORI.trim() || null,
        LOGO_URL: formData.LOGO_URL.trim() || null,
        SIRA: parseInt(formData.SIRA, 10) || 0,
        AKTIF: !!formData.AKTIF,
      };

      const response = isEditing
        ? await fetch(`${BASE_URL}/HABER_KAYNAKLARI(${formData.ID})`, {
            method: "PATCH", headers: getAuthHeaders(), body: JSON.stringify(payload),
          })
        : await fetch(`${BASE_URL}/HABER_KAYNAKLARI`, {
            method: "POST", headers: getAuthHeaders(), body: JSON.stringify(payload),
          });

      // Sunucu KOD tekilliğini kontrol ediyor; sessizce yutulursa panelde
      // "kaydettim" yazıp liste değişmemiş görünürdü.
      if (!response.ok) throw new Error(await hataMesaji(response, "Kayıt sırasında hata oluştu."));

      toggleModal();
      fetchKaynaklar();
      bildir(isEditing ? "Kaynak güncellendi." : "Kaynak eklendi.");
    } catch (err) {
      setError(err.message || "Kayıt sırasında hata oluştu.");
    } finally {
      setKaydediliyor(false);
    }
  };

  /** Aktiflik satır üstünden değişir: en sık yapılan işlem, modal açtırmaya değmez. */
  const toggleAktif = async (kaynak) => {
    const yeni = !kaynak.AKTIF;
    if (!yeni && !window.confirm(
      `"${kaynak.AD}" pasife alınacak.\n\nBu kaynaktan gelen TÜM haberler uygulamada anında görünmez olur. Devam edilsin mi?`
    )) return;

    try {
      const response = await fetch(`${BASE_URL}/HABER_KAYNAKLARI(${kaynak.ID})`, {
        method: "PATCH", headers: getAuthHeaders(), body: JSON.stringify({ AKTIF: yeni }),
      });
      if (!response.ok) throw new Error(await hataMesaji(response, "Durum değiştirilemedi."));
      setKaynaklar((onceki) =>
        onceki.map((k) => (k.ID === kaynak.ID ? { ...k, AKTIF: yeni } : k))
      );
      bildir(yeni ? "Kaynak aktifleştirildi." : "Kaynak pasife alındı.");
    } catch (err) {
      setError(err.message || "Durum değiştirilemedi.");
    }
  };

  const handleDelete = async (kaynak) => {
    if (!window.confirm(
      `"${kaynak.AD}" kalıcı olarak silinecek.\n\nHaberi olan kaynak silinemez — bu durumda pasife alın.`
    )) return;
    try {
      const response = await fetch(`${BASE_URL}/HABER_KAYNAKLARI(${kaynak.ID})`, {
        method: "DELETE", headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error(await hataMesaji(response, "Silme sırasında hata oluştu."));
      fetchKaynaklar();
      bildir("Kaynak silindi.");
    } catch (err) {
      setError(err.message || "Silme sırasında hata oluştu.");
    }
  };

  const columns = useMemo(() => [
    {
      header: "Sıra",
      accessorKey: "SIRA",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => <Badge color="light" className="text-dark px-3">{cell.getValue() ?? 0}</Badge>,
    },
    {
      header: "Kaynak",
      accessorKey: "AD",
      enableSorting: true,
      enableColumnFilter: true,
      cell: (cellProps) => {
        const k = cellProps.row.original;
        return (
          <div className="d-flex align-items-center gap-2">
            {k.LOGO_URL ? (
              <img
                src={k.LOGO_URL}
                alt=""
                style={{ height: 24, width: 24, objectFit: "contain", borderRadius: 4 }}
                onError={(e) => { e.target.style.display = "none"; }}
              />
            ) : null}
            <div>
              <div className="fw-semibold">{k.AD}</div>
              <div className="text-muted small">{k.KOD}</div>
            </div>
          </div>
        );
      },
    },
    {
      header: "Kategori",
      accessorKey: "KATEGORI",
      enableSorting: true,
      enableColumnFilter: true,
      cell: (cell) => cell.getValue()
        ? <Badge color="info" className="bg-info-subtle text-info">{cell.getValue()}</Badge>
        : <span className="text-muted">-</span>,
    },
    {
      header: "RSS Adresi",
      accessorKey: "RSS_URL",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => (
        <a
          href={cell.getValue()}
          target="_blank"
          rel="noopener noreferrer"
          className="text-muted small d-block text-truncate"
          style={{ maxWidth: 260 }}
          title={cell.getValue()}
        >
          {cell.getValue()}
        </a>
      ),
    },
    {
      header: "Son Çekim",
      accessorKey: "SON_CEKIM",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cellProps) => {
        const k = cellProps.row.original;
        return (
          <div>
            <div className="small">{formatApiDateTime(k.SON_CEKIM)}</div>
            {k.SON_HATA ? (
              // Hata metni uzun olabiliyor; tam metin title'da.
              <div className="text-danger small text-truncate" style={{ maxWidth: 240 }} title={k.SON_HATA}>
                <i className="bx bx-error-circle me-1" />{k.SON_HATA}
              </div>
            ) : k.SON_CEKIM ? (
              <div className="text-success small"><i className="bx bx-check me-1" />Başarılı</div>
            ) : (
              <div className="text-muted small">Henüz çekilmedi</div>
            )}
          </div>
        );
      },
    },
    {
      header: "Durum",
      accessorKey: "AKTIF",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cellProps) => {
        const k = cellProps.row.original;
        return (
          <Badge
            color={k.AKTIF ? "success" : "secondary"}
            style={{ cursor: "pointer" }}
            onClick={() => toggleAktif(k)}
            title="Durumu değiştirmek için tıklayın"
          >
            {k.AKTIF ? "Aktif" : "Pasif"}
          </Badge>
        );
      },
    },
    {
      header: "İşlemler",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cellProps) => {
        const k = cellProps.row.original;
        return (
          <div className="d-flex gap-2">
            <Button color="primary" size="sm" outline onClick={() => handleEdit(k)} title="Düzenle">
              <i className="bx bx-edit" />
            </Button>
            <Button color="danger" size="sm" outline onClick={() => handleDelete(k)} title="Sil">
              <i className="bx bx-trash" />
            </Button>
          </div>
        );
      },
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  const aktifSayisi = kaynaklar.filter((k) => k.AKTIF).length;
  const hataliSayisi = kaynaklar.filter((k) => k.AKTIF && k.SON_HATA).length;

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="HarmanaGEL" breadcrumbItem="Haber Kaynakları" />

          {successMsg && <Alert color="success">{successMsg}</Alert>}
          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

          {hataliSayisi > 0 && (
            <Alert color="warning">
              <i className="bx bx-error me-1" />
              {hataliSayisi} aktif kaynak son çekimde hata verdi. RSS adresi değişmiş olabilir —
              "Son Çekim" sütunundaki hata metnine bakın.
            </Alert>
          )}

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h4 className="card-title mb-1">Haber Kaynakları (RSS)</h4>
                      <p className="text-muted small mb-0">
                        {kaynaklar.length} kaynak · {aktifSayisi} aktif ·
                        {" "}Pasif kaynağın haberleri uygulamada görünmez.
                      </p>
                    </div>
                    <div className="d-flex gap-2">
                      <Button color="light" size="sm" onClick={fetchKaynaklar} title="Yenile">
                        <i className="bx bx-refresh" />
                      </Button>
                      <Button color="primary" size="sm" onClick={() => { setFormData(bosForm); setIsEditing(false); setModal(true); }}>
                        <i className="bx bx-plus me-1" />
                        Yeni Kaynak
                      </Button>
                    </div>
                  </div>

                  {isLoading ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={kaynaklar}
                      isGlobalFilter={true}
                      isPagination={true}
                      isCustomPageSize={true}
                      tableClass="table-bordered align-middle nowrap w-100 mb-0"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      <Modal isOpen={modal} toggle={toggleModal} centered>
        <ModalHeader toggle={toggleModal}>
          {isEditing ? "Kaynağı Düzenle" : "Yeni Haber Kaynağı"}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>KOD *</Label>
                <Input
                  value={formData.KOD}
                  onChange={(e) => setFormData({ ...formData, KOD: e.target.value })}
                  placeholder="TARIMDANHABER"
                  disabled={isEditing}
                />
                <small className="text-muted">
                  Sabit referans; sonradan değiştirilmez. Büyük harf, boşluksuz.
                </small>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Kaynak Adı *</Label>
                <Input
                  value={formData.AD}
                  onChange={(e) => setFormData({ ...formData, AD: e.target.value })}
                  placeholder="Tarımdan Haber"
                />
                <small className="text-muted">Haber kartında rozet olarak görünür.</small>
              </FormGroup>
            </Col>
          </Row>

          <FormGroup>
            <Label>RSS Adresi *</Label>
            <Input
              value={formData.RSS_URL}
              onChange={(e) => setFormData({ ...formData, RSS_URL: e.target.value })}
              placeholder="https://ornek.com/rss"
            />
            <small className="text-muted">
              Job bu adresi çeker. Yanlışsa kaynak "Son Çekim" sütununda hata gösterir,
              diğer kaynaklar etkilenmez.
            </small>
          </FormGroup>

          <FormGroup>
            <Label>Site Adresi</Label>
            <Input
              value={formData.SITE_URL}
              onChange={(e) => setFormData({ ...formData, SITE_URL: e.target.value })}
              placeholder="https://ornek.com"
            />
          </FormGroup>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Varsayılan Kategori</Label>
                <Input
                  type="select"
                  value={formData.KATEGORI}
                  onChange={(e) => setFormData({ ...formData, KATEGORI: e.target.value })}
                >
                  <option value="">(yok)</option>
                  {KATEGORI_SECENEKLERI.map((k) => <option key={k} value={k}>{k}</option>)}
                </Input>
                <small className="text-muted">
                  Haberin kendi RSS kategorisi varsa o kazanır.
                </small>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Sıra</Label>
                <Input
                  type="number"
                  value={formData.SIRA}
                  onChange={(e) => setFormData({ ...formData, SIRA: e.target.value })}
                />
                <small className="text-muted">Küçük olan önce çekilir.</small>
              </FormGroup>
            </Col>
          </Row>

          <FormGroup>
            <Label>Logo URL</Label>
            <Input
              value={formData.LOGO_URL}
              onChange={(e) => setFormData({ ...formData, LOGO_URL: e.target.value })}
              placeholder="https://..."
            />
          </FormGroup>

          <FormGroup check className="mt-3">
            <Input
              type="checkbox"
              id="kaynakAktif"
              checked={!!formData.AKTIF}
              onChange={(e) => setFormData({ ...formData, AKTIF: e.target.checked })}
            />
            <Label check for="kaynakAktif" className="ms-1">Aktif</Label>
            <div className="text-muted small">
              Pasifken job bu kaynağı çekmez ve mevcut haberleri uygulamada görünmez.
            </div>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleModal} disabled={kaydediliyor}>Vazgeç</Button>
          <Button color="primary" onClick={handleSave} disabled={kaydediliyor}>
            {kaydediliyor ? <Spinner size="sm" /> : "Kaydet"}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default HaberKaynaklari;
